import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import pMemoize from 'p-memoize';
import { isLogged } from '@/utils/auth/useAuthStatusMutation';
import fetchData from '@/utils/fetchData';
import useError from '@/hooks/useError';
import { Education } from '@/hooks/webapi/types/Education';
import { EmploymentStatus } from '@/hooks/webapi/types/EmploymentStatus';

const memoFetchData = pMemoize(fetchData, { maxAge: 1000 });

type Props = {
  children: ReactNode;
};

export type Client = {
  id: number;
  number: string;
  status:
    | 'UNKNOWN'
    | 'REGISTERED'
    | 'IDENTIFIED'
    | 'DELETED'
    | 'VALIDATED'
    | 'ACTIVE'
    | 'NOT_IDENTIFIED';
  declaredAddress: {
    fullAddress: string;
    postalCode: string;
    location1: string;
  };
  identityDocumentDateOfExpiry: string;
  actualAddress: {
    fullAddress: string;
    postalCode: string;
    location1: string;
  };
  mobilePhoneVerified: boolean;
  firstName: string;
  lastName: string;
  fullName: string;
  personalId: string;
  identificationNumber: string;
  identityDocumentId: string;
  isDocumentIdentified: boolean;
  dateOfBirth: string;
  age: number;
  identifiedBy:
    | 'BANK'
    | 'MANUAL'
    | 'ID_SERVICE'
    | 'BROKER'
    | 'EMAIL'
    | 'IMMEDIATE_TRANSFER'
    | 'FINTONIC'
    | 'PRESTALO'
    | 'NOT_IDENTIFIED';
  registeredBy:
    | 'WEB'
    | 'OFFLINE'
    | 'BROKER'
    | 'CALL_CENTER'
    | 'LEAD_GENERATOR'
    | 'WEB_OTHER_PRODUCT';
  blocked: boolean;
  identified: boolean;
  bankAccount: string;
  openPrincipalAmount: number;
  homePhone: string;
  validationResolutionDetail:
    | 'PERSONAL_ID_MISSING'
    | 'EXPIRED_IDENTITY_DOCUMENT'
    | 'ERROR'
    | 'CHECK_PAYSLIP'; // NOTE: This is not the full enum, instead only the values currently used on FE side
  productNumber: number; // TODO: change to specific product number types
  additionalAmount: boolean;
  creditLimit: number;
  totalCreditLimit: number;
  hasOpenLoan: boolean;
  education?: Education;
  employer?: {
    employmentStatus?: EmploymentStatus;
    name?: string;
  };
  email: Email;
  mobilePhone: Phone;
  _embedded: {
    mobilePhone: {
      mobilePhone: string;
    };
    email: {
      email: string;
    };
  };
};

export type Phone = {
  mobilePhone: string;
  status: string;
};

export type Email = {
  email: string;
};

export type ClientContextType = {
  client: Client | undefined | null;
  fetchClient: () => Promise<Client | void>;
  isClientFetching: boolean;
};

export const ClientContext = createContext<ClientContextType>(
  {} as ClientContextType,
);

export function ClientProvider({ children }: Props): React.JSX.Element {
  const [client, setClient] = useState<Client | undefined | null>();
  const [isClientFetching, setIsClientFetching] = useState(false);
  const { showError } = useError();

  const isClientLogged = isLogged();

  const fetchClient = useCallback(async (): Promise<Client | void> => {
    try {
      setIsClientFetching(true);
      const fetchedClient = await memoFetchData('/client');
      setClient(fetchedClient);
      return fetchedClient;
    } catch (e) {
      setClient(null);
      showError();
      throw e;
    } finally {
      setIsClientFetching(false);
    }
  }, [showError]);

  useEffect(() => {
    if (isClientLogged && client === undefined) {
      fetchClient();
    }
  }, [isClientLogged, client]);

  const clientContextValue = useMemo(
    () => ({
      client,
      fetchClient,
      isClientFetching,
    }),
    [client, fetchClient],
  );

  return (
    <ClientContext.Provider value={clientContextValue}>
      {children}
    </ClientContext.Provider>
  );
}
